import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../../../store';
import { createOrgRole, updateOrgRole, resetAccessControlActionState } from '../../../../../../modules/accessControl';
import {
  selectCreateOrgRoleLoading,
  selectCreateOrgRoleSuccess,
  selectCreateOrgRoleError,
} from '../../../../../../modules/accessControl/selectors';

interface RoleModalProps {
  open: boolean;
  onClose: () => void;
  role?: any;
}

const RoleModal: React.FC<RoleModalProps> = ({ open, onClose, role }) => {
  const dispatch = useDispatch<AppDispatch>();
  const isCreating = useSelector(selectCreateOrgRoleLoading) as boolean;
  const isSuccess = useSelector(selectCreateOrgRoleSuccess) as boolean;
  const error = useSelector(selectCreateOrgRoleError) as string | null;

  const [formData, setFormData] = useState({
    role_name: '',
    role_description: '',
  });

  useEffect(() => {
    if (role) {
      setFormData({
        role_name: role.role_name || '',
        role_description: role.role_description || '',
      });
    } else {
      setFormData({
        role_name: '',
        role_description: '',
      });
    }
  }, [role, open]);

  useEffect(() => {
    if (isSuccess) {
      onClose();
      dispatch(resetAccessControlActionState());
    }
  }, [isSuccess, onClose, dispatch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (role) {
      dispatch(updateOrgRole({ role_id: role.role_id, data: formData }));
    } else {
      dispatch(createOrgRole(formData));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{role ? 'Edit System Role' : 'Create System Role'}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <>
            <TextField
              fullWidth
              label="Role Name"
              name="role_name"
              value={formData.role_name}
              onChange={handleChange}
              placeholder="e.g. Shift Manager"
            />
            <TextField
              fullWidth
              label="Description"
              name="role_description"
              value={formData.role_description}
              onChange={handleChange}
              multiline
              rows={3}
              placeholder="Enter role responsibilities..."
            />
            {error && (
              <Typography color="error" variant="caption">
                {error as string}
              </Typography>
            )}
          </>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disableElevation
          disabled={Boolean(isCreating)}
          sx={{ backgroundColor: '#5E8E7E', '&:hover': { backgroundColor: '#4A7A6A' } }}
        >
          {isCreating ? 'Saving...' : 'Save Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleModal;
